import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import { useAuth } from '../../contexts/AuthContext';
import { useUniverseTheme } from '../../contexts/UniverseThemeContext';
import animatorDashboardService from '../../services/animatorDashboardService';
import { canUseAiScenes } from '../../utils/staffPermissions';
import AnimatorDashboardStats from './components/AnimatorDashboardStats';
import AnimatorThreadRow from './components/AnimatorThreadRow';
import AnimatorQuickActions from './components/AnimatorQuickActions';
import styles from './AnimatorDashboard.module.css';

const AnimatorDashboard = () => {
  const { user } = useAuth();
  const { currentUniverse } = useUniverseTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mineOnly, setMineOnly] = useState(false);

  const universeSlug = useMemo(
    () => (currentUniverse && currentUniverse !== 'portal' ? currentUniverse : null),
    [currentUniverse],
  );

  const hasAccess = canUseAiScenes(user);

  const fetchDashboard = useCallback(async () => {
    if (!hasAccess) return;

    setLoading(true);
    setError(null);
    try {
      const response = await animatorDashboardService.getDashboard({
        universe: universeSlug,
        mineOnly,
        limit: 20,
      });
      setData(response);
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible de charger l\'espace animateur.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasAccess, universeSlug, mineOnly]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  const breadcrumbItems = [
    { name: 'Accueil', url: '/', icon: 'home' },
    { name: 'Espace animateur', url: null, icon: 'category' },
  ];

  if (loading && !data) {
    return (
      <Layout>
        <Loading message="Chargement de l'espace animateur..." />
      </Layout>
    );
  }

  if (error && !data) {
    return (
      <Layout>
        <ErrorMessage message={error} onRetry={fetchDashboard} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Breadcrumb items={breadcrumbItems} />
      <div className={styles.wrap}>
        <header>
          <h1 className={styles.title}>Espace animateur</h1>
          <p className={styles.lead}>
            Suivez les scènes RP animées en cours, consultez vos statistiques et accédez aux outils d&apos;animation.
          </p>
        </header>

        <AnimatorDashboardStats stats={data?.stats} />

        <div className={styles.filtersContainer}>
          <button
            type="button"
            className={`${styles.filterButton} ${!mineOnly ? styles.filterButtonActive : ''}`}
            onClick={() => setMineOnly(false)}
          >
            Toutes les animations
          </button>
          <button
            type="button"
            className={`${styles.filterButton} ${mineOnly ? styles.filterButtonActive : ''}`}
            onClick={() => setMineOnly(true)}
          >
            Mes animations
          </button>
          {universeSlug && (
            <p className={styles.universeHint}>
              Filtre univers :
              {' '}
              {data?.animatedThreads?.[0]?.universe?.name || universeSlug}
            </p>
          )}
        </div>

        {loading && data && (
          <p className={styles.universeHint}>Mise à jour…</p>
        )}

        <div className={styles.layout}>
          <div className={styles.column}>
            <section className={styles.section}>
              <header className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>
                  {mineOnly ? 'Mes RP animés en cours' : 'RP avec animation en cours'}
                </h2>
              </header>
              {data?.animatedThreads?.length ? (
                <div className={styles.list}>
                  {data.animatedThreads.map((thread) => (
                    <AnimatorThreadRow key={thread.id} thread={thread} />
                  ))}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  {mineOnly
                    ? 'Vous n\'animez aucune scène RP en cours pour le moment.'
                    : 'Aucune scène RP animée en cours sur cet univers.'}
                </div>
              )}
            </section>
          </div>

          <div className={styles.column}>
            <AnimatorQuickActions universeSlug={universeSlug} />
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AnimatorDashboard;
